class CollapsibleTab extends HTMLElement {
	constructor() {
		super()

		this.init()
	}

	disconnectedCallback() {
		this.destroy()
	}

	init() {
		this.setDefaultData()
		this.attachEvents()

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.classList.add(this.expandedClass)
			this.setExpandedAria()
		} else {
			this.content.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria()
		}

		this.content.removeAttribute('hidden')
	}

	setDefaultData() {
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'

		this.transitionendEvent = this.transitionendEventName()

		this.trigger = this.querySelector('[data-trigger]')
		this.content = this.querySelector('[data-content]')

		this.collapsedHeight = '0px'
		this.defaultElements = ['a', 'button', 'input:not(.focus-none)', '[data-trigger]']
		this.oneAtATime = true

		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)
	}

	attachEvents() {
		this.trigger.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-container]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()
		this.setAttribute('open', true)
	}

	close() {
		this.selected = false
		this.collapse()
		this.removeAttribute('open')
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.content.style.transition = '0'
						this.content.style.height = 'auto'

						requestAnimationFrame(() => {
							this.content.style.height = null
							this.content.style.transition = null
							this.setExpandedAria()
							this.trySetTabIndex(this.content, 0)
						})
					})
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		this.content.style.height = this.content.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.setCollapsedAria()
					this.classList.add(this.collapsedClass)
					this.trySetTabIndex(this.content, -1)
				}
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.content.style.transition = '0'
			this.content.style.height = this.content.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.content.style.transition = null
				this.content.style.height = this.collapsedHeight
			})
		})
	}

	destroy() {
		this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
		this.content && this.content.removeAttribute('aria-hidden')
		this.content && (this.content.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	trySetTabIndex(parentElement, tabIndex) {
		const elements = parentElement.querySelectorAll(this.defaultElements)

		elements &&
			elements.forEach((e) => {
				e.setAttribute('tabindex', tabIndex)
			})
	}

	setExpandedAria() {
		this.trigger.setAttribute('aria-expanded', 'true')
		this.content.setAttribute('aria-hidden', 'false')
	}

	setCollapsedAria() {
		this.trigger.setAttribute('aria-expanded', 'false')
		this.content.setAttribute('aria-hidden', 'true')
	}
}

customElements.define('l-collapsible-tab', CollapsibleTab)

/************************************************************************************

PRODUCT SECTION COMPONENT: l-product-media-gallery

*************************************************************************************/
if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()

				this.sectionId = this.dataset.sectionId
			}

			connectedCallback() {
				this.setupCarousel()
				this.setDynamicHeight()

				const debounceSetHeight = debounce(() => {
					this.setDynamicHeight()
				}, 50)

				window.addEventListener('resize', debounceSetHeight)

				document.addEventListener('shapes:product:variantchange', (event) => {
					const { variant } = JSON.parse(JSON.stringify(event.detail))

					this.onVariantChange(variant.id)
				})
			}

			setupCarousel() {
				/*** Thumbnails carousel ***/
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
				this.thumbnailNavigationNext = this.thumbnailCarousel.querySelector('[data-next]')
				this.thumbnailNavigationPrev = this.thumbnailCarousel.querySelector('[data-prev]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new Swiper(this.thumbnailCarousel, {
					slidesPerView: 'auto',
					spaceBetween: 8,
					direction: 'horizontal',
					navigation: {
						nextEl: this.thumbnailNavigationNext,
						prevEl: this.thumbnailNavigationPrev,
					},
					breakpoints: {
						0: {
							direction: 'horizontal',
						},
						990: {
							direction: 'vertical',
						},
					},
				})

				/*** Main carousel setup ***/
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					centeredSlides: true,
					centeredSlidesBounds: true,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				/* Setup controller */
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
			}

			setDynamicHeight() {
				this.style.setProperty('--l-product-thumbnail-carousel-height', `${this.mainCarousel.scrollHeight}px`)
			}

			getVariantMediaData() {
				return JSON.parse(this.querySelector('.js-product-variants').textContent) || null
			}

			onVariantChange(selectedVariantId) {
				this.variantData = this.variantData || this.getVariantMediaData()

				const matchMediaId = this.variantData.find((variant) => variant.id == selectedVariantId)?.featured_media?.id

				this.mediaSlides.forEach((mediaSlide, index0) => {
					const mediaId = mediaSlide.dataset.media

					if (mediaId == matchMediaId) {
						this.mainCarouselSwiper.slideTo(index0, true)
					}
				})
			}
		}
	)
}

/************************************************************************************

PRODUCT SECTION COMPONENT: l-swiper-carousel

*************************************************************************************/
class SwiperCarousel extends HTMLElement {
	constructor() {
		super()
	}

	carouselLibraryLoaded = undefined

	connectedCallback() {
		window.Swiper && this.setupCarousel()
	}

	setupCarousel() {
		this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

		if (!this.carouselConfig) return

		this.classList.remove('is-placeholder')
		this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
	}
}

customElements.define('l-swiper-carousel', SwiperCarousel)

/************************************************************************************

PRODUCT SECTION COMPONENT: l-pair-well-with

*************************************************************************************/
class ProductPairWellWith extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.addToCartBtnEls = this.querySelectorAll('[data-atc]')
		this.variantMedia = this.querySelectorAll('[data-media-variant]')
		this.variantPrices = this.querySelectorAll('[data-price]')

		this.setupAddToCart()
		this.onVariantChange()
	}

	onVariantChange() {
		this.addEventListener('input', (event) => {
			const selectedVariantId = event.target.value

			this.variantMedia.forEach((variantMedia) => {
				const mediaId = variantMedia.dataset.mediaVariant
				variantMedia.classList.toggle('is-active', mediaId == selectedVariantId)
			})

			this.variantPrices.forEach((variantPrice) => {
				const variantId = variantMedia.dataset.price
				variantPrice.classList.toggle('l-hidden', variantId != selectedVariantId)
			})
		})
	}

	setupAddToCart() {
		this.addToCartBtnEls.forEach((addToCartBtnEl) => {
			addToCartBtnEl.addEventListener('click', (event) => {
				event.preventDefault()

				addToCartBtnEl.textContent = 'Adding...'
				addToCartBtnEl.classList.add('is-loading')

				liveRegion(window.theme.strings.loading)
				const formData = new FormData()
				let modalCart = theme.settings.cart_type === 'modal'
				const config = fetchConfigDefaults('javascript')

				this.selectedVariantId =
					this.querySelector('select')?.value ||
					this.querySelector('input:checked')?.value ||
					this.dataset.selectedVariantId
				formData.append('id', this.selectedVariantId)

				if (modalCart) {
					formData.append('sections', 'cart-items,cart-footer,cart-item-count')
					formData.append('sections_url', window.location.pathname)
				}

				config.body = formData
				config.headers['X-Requested-With'] = 'XMLHttpRequest'
				delete config.headers['Content-Type']

				fetch(`${theme.routes.cart_add_url}`, config)
					.then((res) => res.json())
					.then((data) => {
						this.loading = false
						this.addedToCart = true

						if (modalCart) {
							document.body.dispatchEvent(
								new CustomEvent('shapes:modalcart:afteradditem', {
									bubbles: true,
									detail: { response: data },
								})
							)
						}
					})
					.catch((error) => {})
					.finally(() => {
						addToCartBtnEl.classList.remove('is-loading')
						addToCartBtnEl.textContent = addToCartBtnEl.dataset.text
					})
			})
		})
	}
}

customElements.define('l-pair-well-with', ProductPairWellWith)

/************************************************************************************

CUSTOMIZE FUNCTIONALITIES

*************************************************************************************/
function goToCheckoutPage() {
	window.location.href = `${window.routes.cart_url}/checkout`
}

window.addEventListener('DOMContentLoaded', () => {
	function initializeStickyHeader() {
		const header = document.getElementById('shopify-section-header')
		if (!header) return

		const handleScroll = () => {
			header.classList.toggle('is-sticky', window.scrollY > header.offsetHeight + 50)
			debounce(() => {
				header.classList.toggle('is-show', window.scrollY > header.offsetHeight + 50)
			}, 50)()
		}

		// Set default
		header.style.setProperty('--l-header-height', `${header.offsetHeight}px`)
		header.classList.toggle('is-sticky', window.scrollY > header.offsetHeight + 50)
		header.classList.toggle('is-show', window.scrollY > header.offsetHeight + 50)

		// Set event
		window.addEventListener('scroll', debounce(handleScroll, 50))
	}

	initializeStickyHeader()
})

function shippingBar() {
	for (let index = 0; index < 10; index++) {
		window.debounce(() => {
			const fsb_container = document.getElementById('fsb_container')
			if (!fsb_container) return

			const header = document.getElementById('shopify-section-header').querySelector('.site-header')
			header.insertAdjacentElement('afterbegin', fsb_container)

			fsb_container.classList.add('is-active')
		}, 300 * index)()
	}
}

shippingBar()

